import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  getWatchBrandById,
  getReferencesCountForBrand,
  getCalibresCountForBrand,
  getRecentReferencesForBrand,
} from '../services/firebase';
import { getBrandReferenceCount, isAlgoliaConfigured } from '../services/algolia';
import type { BrandPortalEditableFields } from '../services/firebase';
import { Modal } from '../components/common/Modal';
import { TrademarkNotice } from '../components/common/TrademarkNotice';
import { EditMyBrandPanel } from '../components/BrandPortal/EditMyBrandPanel';
import { MyReferencesPanel } from '../components/BrandPortal/MyReferencesPanel';
import { MyRequestsPanel } from '../components/BrandPortal/MyRequestsPanel';
import type { WatchBrand, WatchReference } from '../types';

/**
 * Brand Portal dashboard (Phase 1.2).
 *
 * A read-only landing surface for brand owners: header, metric cards, an "about
 * this brand" panel, and a recent-activity list. No editing or mutating actions
 * — those arrive in Phase 2. Every read is scoped to the `:brandId` the signed-in
 * user holds a membership for; navigating to a brand they don't own bounces them
 * back to the root router (see `isBrandMember` gate below).
 */

type CountState = number | 'loading' | 'error';

function formatDate(ts?: { toDate: () => Date }): string {
  if (!ts || typeof ts.toDate !== 'function') return '—';
  try {
    return ts.toDate().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function MetricCard({
  value,
  label,
  hint,
}: {
  value: React.ReactNode;
  label: string;
  hint?: string;
}) {
  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-5'>
      <div className='text-3xl font-bold text-gray-900'>{value}</div>
      <div className='mt-1 text-sm font-medium text-gray-600'>{label}</div>
      {hint && <div className='mt-0.5 text-xs text-gray-400'>{hint}</div>}
    </div>
  );
}

function renderCount(state: CountState): React.ReactNode {
  if (state === 'loading') {
    return <span className='inline-block h-7 w-12 bg-gray-100 rounded animate-pulse' />;
  }
  if (state === 'error') return '—';
  return state.toLocaleString('en-GB');
}

export function BrandPortalPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const {
    user,
    loading,
    isAuthenticated,
    isBrandMember,
    isBrandAdmin,
    ownedBrandIds,
    logout,
  } = useAuth();

  const [brand, setBrand] = useState<WatchBrand | null>(null);
  const [refCount, setRefCount] = useState<CountState>('loading');
  const [calibreCount, setCalibreCount] = useState<CountState>('loading');
  const [recentRefs, setRecentRefs] = useState<WatchReference[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [descExpanded, setDescExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [recentActivityOpen, setRecentActivityOpen] = useState(true);

  const isMember = brandId ? isBrandMember(brandId) : false;

  // Load brand record + counts + recent activity, all scoped to this brand.
  // Guarded by `isMember` so we never fire reads for a brand the user can't see.
  useEffect(() => {
    let cancelled = false;
    if (!brandId || !isMember) return;

    setBrand(null);
    setRefCount('loading');
    setCalibreCount('loading');
    setRecentRefs([]);
    setRecentLoading(true);

    getWatchBrandById(brandId)
      .then((b) => {
        if (!cancelled) setBrand(b);
      })
      .catch(() => {
        if (!cancelled) setBrand(null);
      });

    // References count: prefer Algolia (cheap nbHits), fall back to a Firestore
    // server-side count, and only show "—" if both fail.
    const loadRefCount = async (): Promise<number> => {
      if (isAlgoliaConfigured()) {
        try {
          return await getBrandReferenceCount(brandId);
        } catch {
          // fall through to Firestore
        }
      }
      return getReferencesCountForBrand(brandId);
    };
    loadRefCount()
      .then((c) => {
        if (!cancelled) setRefCount(c);
      })
      .catch(() => {
        if (!cancelled) setRefCount('error');
      });

    getCalibresCountForBrand(brandId)
      .then((c) => {
        if (!cancelled) setCalibreCount(c);
      })
      .catch(() => {
        if (!cancelled) setCalibreCount('error');
      });

    getRecentReferencesForBrand(brandId, 10)
      .then((refs) => {
        if (!cancelled) setRecentRefs(refs);
      })
      .catch(() => {
        if (!cancelled) setRecentRefs([]);
      })
      .finally(() => {
        if (!cancelled) setRecentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [brandId, isMember]);

  // Merge the just-saved fields into local brand state so the dashboard reflects
  // the edit immediately (the callable wrote the same whitelisted fields).
  const handleSaved = (updates: BrandPortalEditableFields) => {
    setBrand((prev) =>
      prev
        ? {
            ...prev,
            ...updates,
            founded: updates.founded ?? undefined,
          }
        : prev,
    );
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-100'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto'></div>
          <p className='mt-4 text-gray-600'>Loading...</p>
        </div>
      </div>
    );
  }

  // Not signed in, or no/unknown brand in the URL → let the root router decide
  // where this user belongs (login screen, admin home, or their own portal).
  if (!isAuthenticated || !brandId) {
    return <Navigate to='/' replace />;
  }

  // Signed in but not a member of THIS brand → bounce to root, which re-routes
  // them to a brand they do belong to (or the admin home / access-denied).
  if (!isMember) {
    return <Navigate to='/' replace />;
  }

  const role = isBrandAdmin(brandId) ? 'Brand Admin' : 'Brand Member';
  const brandName = brand?.name ?? brandId;
  const logo = brand?.logo && brand.logo.startsWith('http') ? brand.logo : null;
  const website = brand?.website || brand?.mainWebsite || null;
  const description = brand?.description ?? '';
  const DESC_PREVIEW_LEN = 200;
  const needsTruncation = description.length > DESC_PREVIEW_LEN;
  const shownDescription =
    needsTruncation && !descExpanded
      ? `${description.slice(0, DESC_PREVIEW_LEN).trimEnd()}…`
      : description;

  return (
    <div className='min-h-screen bg-gray-100'>
      {/* Header */}
      <header className='bg-white border-b border-gray-200'>
        <div className='max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4'>
          <div className='flex items-center gap-4 min-w-0'>
            {logo && (
              <img
                src={logo}
                alt={`${brandName} logo`}
                className='h-12 w-12 rounded-lg object-contain bg-gray-50 border border-gray-200 flex-shrink-0'
              />
            )}
            <div className='min-w-0'>
              <h1 className='text-xl font-bold text-gray-900 truncate'>
                {brandName}
              </h1>
              <p className='text-sm text-gray-500'>Brand Portal</p>
            </div>
          </div>
          <div className='flex items-center gap-4 flex-shrink-0'>
            <div className='text-right hidden sm:block'>
              <p className='text-sm font-medium text-gray-700 truncate max-w-[16rem]'>
                {user?.email}
              </p>
              <span className='inline-block mt-0.5 text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full'>
                {role}
              </span>
            </div>
            <button
              onClick={() => logout()}
              className='px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50'
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className='max-w-6xl mx-auto px-6 py-8 space-y-8'>
        {/* Metric cards */}
        <section className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
          <MetricCard value={renderCount(refCount)} label='References' />
          <MetricCard value={renderCount(calibreCount)} label='Calibres' />
          <MetricCard value='0' label='Promotions' hint='Coming soon' />
          <MetricCard value='—' label='Earnings' hint='Coming soon' />
        </section>

        <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
          {/* Brand details panel */}
          <section className='lg:col-span-1'>
            <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-base font-semibold text-gray-900'>
                  About this brand
                </h2>
                <button
                  type='button'
                  onClick={() => setEditOpen(true)}
                  disabled={!brand}
                  className='px-3 py-1.5 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50'
                >
                  Edit Brand
                </button>
              </div>
              <dl className='space-y-3 text-sm'>
                <div>
                  <dt className='text-gray-500'>Name</dt>
                  <dd className='text-gray-900 font-medium'>{brandName}</dd>
                </div>
                {brand?.country && (
                  <div>
                    <dt className='text-gray-500'>Country</dt>
                    <dd className='text-gray-900'>{brand.country}</dd>
                  </div>
                )}
                {brand?.founded && (
                  <div>
                    <dt className='text-gray-500'>Founded</dt>
                    <dd className='text-gray-900'>{brand.founded}</dd>
                  </div>
                )}
                {description && (
                  <div>
                    <dt className='text-gray-500'>Description</dt>
                    <dd className='text-gray-700 whitespace-pre-line'>
                      {shownDescription}
                      {needsTruncation && (
                        <button
                          onClick={() => setDescExpanded((v) => !v)}
                          className='ml-1 text-brand-600 hover:text-brand-700 font-medium'
                        >
                          {descExpanded ? 'Read less' : 'Read more'}
                        </button>
                      )}
                    </dd>
                  </div>
                )}
                {website && (
                  <div>
                    <dt className='text-gray-500'>Website</dt>
                    <dd>
                      <a
                        href={website}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-brand-600 hover:text-brand-700 break-all'
                      >
                        {website.replace(/^https?:\/\//, '')}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </section>

          {/* Recent activity */}
          <section className='lg:col-span-2'>
            <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
              <button
                type='button'
                onClick={() => setRecentActivityOpen((v) => !v)}
                aria-expanded={recentActivityOpen}
                className={`w-full flex items-center justify-between gap-4 px-6 py-4 text-left ${
                  recentActivityOpen ? 'border-b border-gray-200' : ''
                }`}
              >
                <div>
                  <h2 className='text-base font-semibold text-gray-900'>
                    Recent activity
                  </h2>
                  <p className='text-xs text-gray-400 mt-0.5'>
                    Most recently updated references
                  </p>
                </div>
                <svg
                  className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${
                    recentActivityOpen ? 'rotate-180' : ''
                  }`}
                  viewBox='0 0 20 20'
                  fill='currentColor'
                  aria-hidden='true'
                >
                  <path
                    fillRule='evenodd'
                    d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z'
                    clipRule='evenodd'
                  />
                </svg>
              </button>
              {!recentActivityOpen ? null : recentLoading ? (
                <div className='px-6 py-10 text-center text-sm text-gray-400'>
                  Loading references…
                </div>
              ) : recentRefs.length === 0 ? (
                <div className='px-6 py-10 text-center text-sm text-gray-500'>
                  No references yet.
                </div>
              ) : (
                <ul className='divide-y divide-gray-100'>
                  {recentRefs.map((ref) => (
                    <li key={ref.id}>
                      <button
                        type='button'
                        onClick={() => {
                          /* Phase 2: no ref detail screen yet — noop. */
                        }}
                        className='w-full flex items-center justify-between gap-4 px-6 py-3 text-left hover:bg-gray-50'
                      >
                        <div className='min-w-0'>
                          <p className='text-sm font-medium text-gray-900 truncate'>
                            {ref.reference || ref.id}
                          </p>
                          <p className='text-xs text-gray-500 truncate'>
                            {ref.model || ref.productName || '—'}
                          </p>
                        </div>
                        <span className='text-xs text-gray-400 flex-shrink-0'>
                          {formatDate(ref.updatedAt)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        {/* Your References — full-width read-only list (Phase 2.2b) */}
        <section>
          <MyReferencesPanel brandId={brandId} />
        </section>

        {/* Your Requests — entity-request inbox across all brands (Phase 2.5c).
            No tab/sidebar nav exists yet, so this mirrors MyReferencesPanel's
            registration as a full-width section. */}
        <section>
          <MyRequestsPanel
            userBrandIds={ownedBrandIds}
            brandNames={brandName ? { [brandId]: brandName } : undefined}
          />
        </section>
      </main>

      {brand && (
        <Modal
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
          title='Edit Brand'
          size='lg'
        >
          <EditMyBrandPanel
            brandId={brandId}
            brand={brand}
            onSaved={handleSaved}
            onClose={() => setEditOpen(false)}
          />
        </Modal>
      )}
      <TrademarkNotice className='max-w-6xl mx-auto px-6 pb-6' />
    </div>
  );
}
